import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { themeStyle } from "../../context/BrandingContext";
import { PageHeader, PageSkeleton, KpiCard, ProgressBar, StatusBadge, Avatar, Section } from "../../components/fields";
import { UserCheck, UserX, ClipboardCheck, Flag, Users } from "lucide-react";

const STATUS_LABEL = { green: "Documented today", gray: "No entry yet", amber: "Concern flagged", red: "Attention required" };
const STATUS_TONE = { green: "success", gray: "neutral", amber: "warning", red: "critical" };

export default function SchoolOverview() {
  const [data, setData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolId, setSchoolId] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSwitch = user?.role === "tag_admin" || (user?.role === "supervisor" && (user?.school_ids || []).length > 1);

  useEffect(() => { if (canSwitch) api.get("/schools").then((r) => setSchools(r.data)); }, [canSwitch]);
  useEffect(() => {
    setData(null);
    api.get("/overview/school", { params: schoolId ? { school_id: schoolId } : {} }).then((r) => setData(r.data));
  }, [schoolId]);

  if (!data) return <PageSkeleton />;

  const documentedToday = data.students.filter((s) => s.documented_today > 0).length;
  const openFlags = data.students.reduce((a, s) => a + s.open_flags, 0);
  const aidesPresent = data.aides.filter((a) => a.present_today).length;
  const totalBlocks = data.students.reduce((a, s) => a + s.total_blocks_today, 0);
  const doneBlocks = data.students.reduce((a, s) => a + s.documented_today, 0);
  const docRate = totalBlocks ? Math.round((doneBlocks / totalBlocks) * 100) : null;

  return (
    <div className={`space-y-5 stagger ${data.school_theme ? "sf-themed" : ""}`} style={themeStyle(data.school_theme)}>
      <PageHeader
        title="Overview"
        subtitle={`${data.school_name || "All TAG-supported students"} · ${data.date}`}
        actions={
          <div className="flex items-center gap-3">
            {canSwitch && schools.length > 1 && (
              <select data-testid="school-switcher" value={schoolId || data.school_id || ""} onChange={(e) => setSchoolId(e.target.value)}
                className="rounded-[10px] border border-[#E5E9EE] bg-white px-3 py-2 text-sm font-semibold text-[#132238] focus:outline-none focus:ring-2 focus:ring-[#087F5B]/40">
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            {data.school_logo && (
              <img src={data.school_logo} alt={data.school_name} data-testid="school-logo"
                className="h-10 rounded-xl border border-[#E5E9EE] bg-white p-1 object-contain" />
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KpiCard testId="stat-documented-today" label="Students Documented" value={`${documentedToday}/${data.students.length}`} icon={ClipboardCheck} tone="green" sub="Today" />
        <KpiCard testId="stat-doc-rate" label="Documentation Today" value={docRate != null ? `${docRate}%` : "—"} ring={docRate ?? 0} sub={`${doneBlocks} of ${totalBlocks} blocks`} />
        {data.flags_hidden ? (
          <>
            <KpiCard testId="stat-incidents-today" label="Incidents Today" value={data.today_incidents ?? 0} icon={Flag} tone={data.today_incidents > 0 ? "amber" : "green"} sub="Behavior / concern entries" />
            <KpiCard testId="stat-goal-checks-today" label="Goal Checks Today" value={data.today_goal_checks ?? 0} icon={Users} tone="blue" sub="Entries recorded" />
          </>
        ) : (
          <>
            <KpiCard testId="stat-open-flags" label="Open Flags" value={openFlags} icon={Flag} tone={openFlags > 0 ? "amber" : "green"} sub={openFlags > 0 ? "Awaiting resolution" : "All clear"} />
            <KpiCard testId="stat-aides-present" label="Aides Active" value={`${aidesPresent}/${data.aides.length}`} icon={Users} tone={aidesPresent === data.aides.length ? "green" : "amber"} sub="Today" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.students.map((s) => (
          <button key={s.id} data-testid={`overview-student-${s.id}`} onClick={() => navigate(`/students/${s.id}/dashboard`)}
            className={`sf-card-hover text-left bg-white border rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)] ${
              s.status_color === "red" ? "border-red-200" : s.status_color === "amber" ? "border-amber-200" : "border-[#E5E9EE]"
            }`}>
            <div className="flex items-center gap-3">
              <Avatar name={s.name} tone="mint" />
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-[15px] font-semibold text-[#132238] truncate">{s.name}</h3>
                <p className="text-xs text-[#667085]">{s.grade ? `Grade ${s.grade} · ` : ""}{s.aides.map((a) => a.name).join(", ") || "Unassigned"}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <StatusBadge tone={STATUS_TONE[s.status_color]}>{STATUS_LABEL[s.status_color]}</StatusBadge>
              <span className="text-xs text-[#667085]">{s.documented_today}/{s.total_blocks_today} blocks</span>
            </div>
            <div className="mt-2.5">
              <ProgressBar value={s.total_blocks_today ? (s.documented_today / s.total_blocks_today) * 100 : 0} height="h-1.5" />
            </div>
            {s.open_flags > 0 && (
              <p className="mt-3 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 inline-block">
                {s.open_flags} open flag{s.open_flags > 1 ? "s" : ""}{s.critical_flag ? " · CRITICAL" : ""}
              </p>
            )}
          </button>
        ))}
      </div>

      <Section title="Aide Coverage Today">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {data.aides.map((a) => (
            <div key={a.id} data-testid={`coverage-aide-${a.id}`} className="flex items-center gap-3 border border-[#E5E9EE] rounded-xl p-3.5">
              {a.present_today ? <UserCheck size={19} className="text-[#087F5B]" /> : <UserX size={19} className="text-slate-300" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#132238]">{a.name}</p>
                <p className="text-xs text-[#667085] truncate">{a.students.join(", ")}</p>
              </div>
              <StatusBadge tone={a.present_today ? "success" : "neutral"}>
                {a.present_today ? `${a.entries_today} entries` : "No entries yet"}
              </StatusBadge>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
